/**
 * Comprehensive Notification Management API Service
 * Handles all notification features including delivery, preferences, templates, 
 * scheduling, history, analytics, and error handling
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Use proxy for development, fallback to config
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
const NOTIFICATION_API = `${BASE_URL}/api/v1/notifications/management`;

// Notification Delivery API
export const notificationDeliveryApi = {
  // Get delivery status
  getDeliveryStatus: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${NOTIFICATION_API}/delivery_status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching delivery status:', error);
      throw error;
    }
  },

  // Send test notification
  sendTestNotification: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${NOTIFICATION_API}/send_test_notification/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }
};

// Notification Preferences API
export const notificationPreferencesApi = {
  // Get preferences
  getPreferences: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${NOTIFICATION_API}/preferences/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching preferences:', error);
      throw error;
    }
  },

  // Update preferences
  updatePreferences: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${NOTIFICATION_API}/update_preferences/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }
};

// Notification Templates API
export const notificationTemplatesApi = {
  // Get templates
  getTemplates: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${NOTIFICATION_API}/templates/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  // Create template
  createTemplate: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${NOTIFICATION_API}/create_template/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }
};

// Notification Scheduling API
export const notificationSchedulingApi = {
  // Get scheduled notifications
  getScheduled: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${NOTIFICATION_API}/scheduled/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled notifications:', error);
      throw error;
    }
  },

  // Schedule notification
  scheduleNotification: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${NOTIFICATION_API}/schedule_notification/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling notification:', error);
      throw error;
    }
  }
};

// Notification History API
export const notificationHistoryApi = {
  // Get history
  getHistory: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${NOTIFICATION_API}/history/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching history:', error);
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${NOTIFICATION_API}/notifications/${notificationId}/mark_read/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all as read
  markAllAsRead: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${NOTIFICATION_API}/mark_all_read/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }
};

// Notification Analytics API
export const notificationAnalyticsApi = {
  // Get analytics
  getAnalytics: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${NOTIFICATION_API}/analytics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }
};

// Notification Error Handling API
export const notificationErrorApi = {
  // Get error logs
  getErrorLogs: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${NOTIFICATION_API}/error_logs/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching error logs:', error);
      throw error;
    }
  }
};

// Real-time notification updates (WebSocket)
export const notificationRealtimeApi = {
  // Connect to WebSocket for real-time updates
  connectWebSocket: (onMessage, onError, onClose) => {
    const token = localStorage.getItem('accessToken');
    const wsUrl = `${BASE_URL.replace('http', 'ws')}/ws/notifications/?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected for notifications');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        onError(error);
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed:', event);
        onClose(event);
      };
      
      return ws;
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      onError(error);
      return null;
    }
  },

  // Send message through WebSocket
  sendMessage: (ws, message) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  },

  // Close WebSocket connection
  disconnect: (ws) => {
    if (ws) {
      ws.close();
    }
  }
};

// Notification testing utilities for development
export const notificationTestingApi = {
  // Test all notification channels
  testAllChannels: async () => {
    try {
      const channels = ['EMAIL', 'SMS', 'WHATSAPP', 'IN_APP'];
      const results = [];
      
      for (const channel of channels) {
        try {
          const result = await notificationDeliveryApi.sendTestNotification({
            channel,
            message: `Test notification via ${channel}`
          });
          results.push({ channel, success: true, result });
        } catch (error) {
          results.push({ channel, success: false, error: error.message });
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error testing all channels:', error);
      throw error;
    }
  },

  // Generate test data
  generateTestData: async () => {
    try {
      const testData = {
        notifications: [],
        templates: [],
        preferences: {}
      };
      
      // Generate test notifications
      for (let i = 0; i < 10; i++) {
        testData.notifications.push({
          title: `Test Notification ${i + 1}`,
          message: `This is test notification number ${i + 1}`,
          channel: ['EMAIL', 'SMS', 'IN_APP'][i % 3],
          status: ['PENDING', 'SENT', 'FAILED'][i % 3]
        });
      }
      
      return testData;
    } catch (error) {
      console.error('Error generating test data:', error);
      throw error;
    }
  }
};

export default {
  notificationDeliveryApi,
  notificationPreferencesApi,
  notificationTemplatesApi,
  notificationSchedulingApi,
  notificationHistoryApi,
  notificationAnalyticsApi,
  notificationErrorApi,
  notificationRealtimeApi,
  notificationTestingApi
};


















